import * as _ from 'lodash-es';

import { InternalEquipment, InternalEquipmentFilter, InternalEquipmentFilterPerformance } from '../model/internalEquipment';
import { Observable, firstValueFrom, from, of } from 'rxjs';

import { DexieService } from './dexieService';
import { EquipmentAutocompleteFilter } from '../model/autocompleteFilter';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { HttpUtils } from '../utils/httpUtils';
import { Injectable } from '@angular/core';
import { InternalEquipmentStatusEnum } from '../model/internalEquipmentStatus';
import { OnlineService } from './online.service';
import { environment } from 'src/environments/environment';
import { map } from 'rxjs/operators';
import { QueryParam, ServiceUtil } from '../utils/serviceUtils';

@Injectable()
export class InternalEquipmentService {

    readonly baseUrl = `${environment.equipmentUrl}/internalEquipment`;
    constructor(private http: HttpClient,
        private dexieService: DexieService,
        private onlineService: OnlineService) {
    }

    findAll(filter: InternalEquipmentFilter): Observable<any> {
        if (this.onlineService.latestOnline) {
            const clonedFilter = _.cloneDeep(filter);

            const url = `${this.baseUrl}/`;

            const customFields = {};

            if (clonedFilter.customFields) {
                (clonedFilter.customFields as Map<number, string>)?.forEach((val, key) => {
                    customFields[key] = val;
                });
            }

            clonedFilter.customFields = customFields;

            return this.http.post(url, clonedFilter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            return from(this.dexieService.internalEquipments.toArray());
        }
    }

    getExcel(filter: InternalEquipmentFilterPerformance): Observable<any> {
        const url = `${this.baseUrl}/excel`;
        const clonedFilter = _.cloneDeep(filter);
        const customFields = {};

        if (clonedFilter.customFields) {
            (clonedFilter.customFields as Map<number, string>)?.forEach((val, key) => {
                customFields[key] = val;
            });
        }

        clonedFilter.customFields = customFields;

        return this.http.post(url, clonedFilter, { responseType: 'blob' });
    }

    findAutocomplete(filter: EquipmentAutocompleteFilter): Observable<any> {
        if (this.onlineService.latestOnline) {
            const url = `${this.baseUrl}/autocomplete`;

            return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            return from(this.dexieService.internalEquipments.where('name').startsWithAnyOfIgnoreCase(filter.query)
                .limit(filter.pageSize || 10).toArray());
        }
    }

    findOne(id: number): Observable<any> {
        if (this.onlineService.latestOnline) {
            const url = `${this.baseUrl}/${id}`;

            return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            return from(this.dexieService.internalEquipments.where({ id }).toArray());
        }
    }

    findOneByName(name: string): Observable<any> {
        if (this.onlineService.latestOnline) {
            const url = `${this.baseUrl}/name/${name}`;

            return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            return from(this.dexieService.internalEquipments.where({ name }).toArray()[0]);
        }
    }

    findOneBySerialNum(serialNum: string): Observable<any> {
        if (this.onlineService.latestOnline) {
            const url = `${this.baseUrl}/serialNum/${serialNum}`;

            return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            return from(new Promise((resolve, reject) => {
                this.dexieService.internalEquipments.where({ serialNum }).toArray().then((arr) => {
                    if (arr[0]) {
                        resolve(arr[0]);
                    } else {
                        reject();
                    }
                }).catch(() => reject());
            }));
        }
    }

    approved(internalEquipment: InternalEquipment, reason: string): Observable<any> {
        let url = `${this.baseUrl}/approved/${internalEquipment.id}`;

        const form = new FormData();
        form.append('equipment', JSON.stringify(internalEquipment));
        form.append('reason', reason);

        return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    save(internalEquipment: InternalEquipment, reason: string): Observable<any> {
        let url = `${this.baseUrl}/`;

        const form = new FormData();
        form.append('equipment', JSON.stringify(internalEquipment));
        form.append('reason', reason);
        if (internalEquipment.id != null) {
            url += internalEquipment.id;

            return this.http.put(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            url += 'create';

            return this.http.post(url, form).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        }
    }

    uploadMassive(data: InternalEquipment[]): Observable<any> {
        const url = `${this.baseUrl}/create/massive`;

        return this.http.post(url, data).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    activate(id: number, activate: boolean, reason: string): Observable<any> {
        return this.findOne(id).pipe(map(item => {
            const eq = item as InternalEquipment;
            eq.expansionDay = null;
            eq.extendedExpirationDate = null;
            eq.idStatus = activate ? InternalEquipmentStatusEnum.EN_USO : InternalEquipmentStatusEnum.FUERA_DE_USO;
            if (eq.performances) {
                eq.performances.forEach(ep => ep.idStatus = activate ? InternalEquipmentStatusEnum.EN_USO : InternalEquipmentStatusEnum.FUERA_DE_USO);
            }
            return this.save(eq, reason);
        }, err => {
            return of(err);
        }));
    }

    downloadPdf(id: number): Observable<any> {
        const url = environment.equipmentUrl + `/report/internalEquipment/${id}`;

        return this.http.get(url, { responseType: 'blob' });
    }

    downloadAuditPdf(id: number): Observable<any> {
        const url = environment.equipmentUrl + `/report/internalEquipment/${id}/audit`;

        return this.http.get(url, { responseType: 'blob' });
    }

    async saveInternalEquipmentToDexie(): Promise<void> {
        const filter = new EquipmentAutocompleteFilter();

        filter.pageIndex = 0;
        filter.pageSize = 20000;
        filter.sortBy = 'name';
        filter.sortDirection = 'asc';
        filter.forExecution = true;

        const res = await firstValueFrom(this.findAutocomplete(filter));
        if (!res || !res?.content) return;

        await this.dexieService.internalEquipments.clear();

        await Promise.all(res.content.map(data => this.dexieService.internalEquipments.put(data, data.id)));
    }

    checkCopyToGroup(id: number, idGroup: number): Observable<any> {
        const url = `${this.baseUrl}/clonate/${id}/toGroup/${idGroup}/check`;

        return this.http.post(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    copyToGroup(id: number, idGroup: number): Observable<any> {
        const url = `${this.baseUrl}/clonate/${id}/toGroup/${idGroup}`;

        return this.http.put(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    findAllPerformance(filter: InternalEquipmentFilterPerformance): Observable<any> {
        if (this.onlineService.latestOnline) {
            const clonedFilter = _.cloneDeep(filter);

            const url =`${this.baseUrl}/filter-performances`;

            const customFields = {};

            (clonedFilter.customFields as Map<number, string>).forEach((val, key) => {
                customFields[key] = val;
            });

            clonedFilter.customFields = customFields;

            return this.http.post(url, clonedFilter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            return from(this.dexieService.internalEquipments.toArray());
        }
    }

    getAllByGroup(idGroup: number, idEquipment: number): Observable<any> {

        if (!this.onlineService.latestOnline) {
            return of([]);
        }

        const url = `${this.baseUrl}/clonate/toGroup/${idGroup}/toEquipment/${idEquipment}/folder`;

        return this.http.post(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    copyToGroupWithIdFolder(id: number, idGroup: number, idFolder: number): Observable<any> {
        const url = `${this.baseUrl}/clonate/${id}/toGroup/${idGroup}/folder/${idFolder}`;

        return this.http.put(url, {}).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadPdfActions(idEquipment: number, documentCode: string): Observable<any> {
        const url = `${this.baseUrl}/${idEquipment}/document?documentCode=${encodeURIComponent(documentCode)}`;

        return this.http.get(url, { responseType: 'blob' });
    }

    downloadPdfIdActionType(idPerformance: number, idActionType: number): Observable<any> {
        const url = `${this.baseUrl}/action/${idPerformance}/${idActionType}`;

        return this.http.get(url, { responseType: 'blob' });
    }

    findOneByNameInternal(name: string, internal: boolean, certifcate: string = "C"): Observable<any> {
        const url = `${this.baseUrl}/name/${name}/certificate`;
        const params: QueryParam[] = [{ key: 'isInternal', value: internal }, {key: 'certificate', value: encodeURIComponent(certifcate)}];
        
        return this.http.get(ServiceUtil.createQueryParams(url, params)).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    getOneByNameClient(name: string, clientName: string): Observable<any> {
        const url = `${this.baseUrl}/name/${name}/client/${clientName}`;
        return this.http.get(url).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    checkMassiveCertificate(equipments: string[]): Observable<any> {
        const url = environment.calibratesUrl + `/externalCalibration/check/certificate-massive`;

        return this.http.post(url, equipments).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    checkEqExternalMassiveCertificate(equipments: Map<string, string>): Observable<any> {
        const url = environment.calibratesUrl + `/externalCalibration/check/external/certificate-massive`;

        const obj: { [key: string]: string } = {};
            equipments.forEach((value, key) => {
            obj[key] = value;
        });

        return this.http.post(url, obj).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
    }

    downloadCertificates(ids: number[], idActions: number[]): Observable<any> {
        const url = `${ environment.equipmentUrl}/internalEquipment/bulk/downloadCertificates`;
        let headers = new HttpHeaders({});

        const form = new FormData();
        form.append('ids', ids.join(';'));
        form.append('idActions', idActions.join(';'))

        return this.http.post(url, form, { responseType: 'blob', headers });
    }

    getEquipmentForPattern(filter: EquipmentAutocompleteFilter): Observable<any> {
        if (this.onlineService.latestOnline) {
            const url = `${this.baseUrl}/autocompleteForPattern`;

            return this.http.post(url, filter).pipe(map(httpRes => HttpUtils.extractData(httpRes)));
        } else {
            return from(this.dexieService.internalEquipments.where('name').startsWithAnyOfIgnoreCase(filter.query)
                .limit(filter.pageSize || 10).toArray());
        }
    }
}

